import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, CircularProgress, Button, Chip, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL } from "../config";
import { FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";

// ─── Subject palette cycling ────────────────────────────────────────────────
const SUBJECT_ACCENTS = [
  { bg: "#EEF0FF", stripe: "#5B4FE8", icon: "📐" },
  { bg: "#FFF0F6", stripe: "#E8488A", icon: "🔬" },
  { bg: "#F0FFF4", stripe: "#22A06B", icon: "📖" },
  { bg: "#FFFBEB", stripe: "#D97706", icon: "🌍" },
  { bg: "#F0F9FF", stripe: "#0284C7", icon: "🎨" },
  { bg: "#FDF4FF", stripe: "#9333EA", icon: "🧮" },
];

const getAccent = (index) => SUBJECT_ACCENTS[index % SUBJECT_ACCENTS.length];

// ─── Animation variants ──────────────────────────────────────────────────────
const containerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.97 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 260, damping: 22 },
  },
};

const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const SubjectCard = ({ subject, index, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const accent = getAccent(index);

  return (
    <motion.div variants={cardVariants}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onClick}
        sx={{
          position: "relative",
          borderRadius: "20px",
          overflow: "hidden",
          background: "#FFFFFF",
          border: "1.5px solid",
          borderColor: hovered ? accent.stripe : "#EBEBF0",
          boxShadow: hovered
            ? `0 12px 40px -8px ${accent.stripe}33, 0 2px 8px rgba(0,0,0,0.06)`
            : "0 2px 12px rgba(0,0,0,0.05)",
          transition: "all 0.28s cubic-bezier(0.34,1.2,0.64,1)",
          transform: hovered ? "translateY(-4px)" : "translateY(0)",
          cursor: "pointer",
          p: 3,
          minHeight: 190,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Animated top stripe */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: hovered ? "5px" : "3px",
            background: accent.stripe,
            transition: "height 0.25s ease",
            borderRadius: "20px 20px 0 0",
          }}
        />

        {/* Icon badge */}
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: "14px",
            background: accent.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 24,
            mb: 2,
            flexShrink: 0,
          }}
        >
          {accent.icon}
        </Box>

        {/* Subject name */}
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: "#1A1A2E",
            textTransform: "capitalize",
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
            mb: 0.5,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {subject.name}
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            color: "#9898A6",
            mb: 2.5,
            fontFamily: "'Inter', sans-serif",
          }}
        >
          Tap to view assignments
        </Typography>

        {/* CTA */}
        <Button
          fullWidth
          disableElevation
          sx={{
            borderRadius: "12px",
            py: 1.1,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: "'Inter', sans-serif",
            letterSpacing: "0.01em",
            background: hovered ? accent.stripe : accent.bg,
            color: hovered ? "#fff" : accent.stripe,
            border: "1.5px solid",
            borderColor: accent.stripe + "55",
            transition: "all 0.22s ease",
            textTransform: "none",
            "&:hover": {
              background: accent.stripe,
              color: "#fff",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          Open Assignments →
        </Button>
      </Box>
    </motion.div>
  );
};

const EmptyState = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
  >
    <Box
      sx={{
        textAlign: "center",
        py: 10,
        px: 4,
        background: "#F8F9FF",
        borderRadius: "24px",
        border: "1.5px dashed #D0CEF0",
      }}
    >
      <Typography sx={{ fontSize: 48, mb: 2 }}>📚</Typography>
      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 700,
          color: "#1A1A2E",
          mb: 1,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        No subjects yet
      </Typography>
      <Typography
        sx={{
          color: "#9898A6",
          fontSize: 14,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        Your subjects will appear here once your class is set up.
      </Typography>
    </Box>
  </motion.div>
);

const LoadingScreen = () => (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
    }}
  >
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
    >
      <CircularProgress size={40} sx={{ color: "#5B4FE8" }} thickness={4} />
    </motion.div>
    <Typography
      sx={{ color: "#9898A6", fontSize: 14, fontFamily: "'Inter', sans-serif" }}
    >
      Loading your subjects…
    </Typography>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const StudentAssignmentHome = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStudentSubjects = useCallback(async () => {
    try {
      setLoading(true);

      const tokenString = localStorage.getItem("authToken");
      const token = JSON.parse(tokenString);
      const classId = localStorage.getItem("instclass");

      if (!classId) {
        toast.error("Class not found. Please log in again.");
        return;
      }

      const response = await axios.get(
        `${API_URL}/instclass/${classId}/subjects/`,
        {
          headers: {
            Authorization: `Bearer ${token.access}`,
          },
        }
      );
      console.log(response.data);

      setSubjects(response.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load subjects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudentSubjects();
  }, [fetchStudentSubjects]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(160deg, #F8F9FF 0%, #FFFFFF 60%)",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <Box
        sx={{
          maxWidth: "1100px",
          mx: "auto",
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 6 },
        }}
      >
        {/* Back button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          style={{ position: "absolute", top: 24, left: 24, zIndex: 10 }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              color: "#475569",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(8px)",
              boxShadow:
                "0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "#fff",
                color: "#0f172a",
                transform: "translateX(-2px)",
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              },
            }}
          >
            <FaArrowLeft size={15} />
          </IconButton>
        </motion.div>
        {/* ── Header ── */}
        <AnimatePresence>
          {!loading && (
            <motion.div
              variants={headerVariants}
              initial="hidden"
              animate="show"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 6,
                }}
              >
                <Box>
                  {/* Eyebrow */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: "#5B4FE8",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#5B4FE8",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      My Assignments
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      fontSize: { xs: 28, sm: 36 },
                      fontWeight: 800,
                      color: "#1A1A2E",
                      letterSpacing: "-0.03em",
                      lineHeight: 1.1,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Your Subjects
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1,
                      color: "#9898A6",
                      fontSize: 14,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Select a subject to view and complete your assignments.
                  </Typography>
                </Box>

                {/* Subject count pill */}
                {subjects.length > 0 && (
                  <Chip
                    label={`${subjects.length} Subject${
                      subjects.length !== 1 ? "s" : ""
                    }`}
                    sx={{
                      background: "#EEF0FF",
                      color: "#5B4FE8",
                      fontWeight: 700,
                      fontSize: 13,
                      px: 1,
                      borderRadius: "10px",
                      fontFamily: "'Inter', sans-serif",
                      height: 36,
                      "& .MuiChip-label": { px: 1.5 },
                    }}
                  />
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Content ── */}
        {loading ? (
          <LoadingScreen />
        ) : subjects.length === 0 ? (
          <EmptyState />
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="show"
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                gap: 3,
              }}
            >
              {subjects.map((subject, index) => (
                <SubjectCard
                  key={subject.id}
                  subject={subject}
                  index={index}
                  onClick={() =>
                    navigate(`/student-assignment/${subject.id}`, {
                      state: { subjectName: subject.name },
                    })
                  }
                />
              ))}
            </Box>
          </motion.div>
        )}

        {/* ── Refresh hint ── */}
        {!loading && subjects.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Box sx={{ textAlign: "center", mt: 6 }}>
              <Typography
                component="span"
                onClick={fetchStudentSubjects}
                sx={{
                  fontSize: 13,
                  color: "#BCBCCA",
                  cursor: "pointer",
                  fontFamily: "'Inter', sans-serif",
                  "&:hover": { color: "#5B4FE8" },
                  transition: "color 0.2s",
                }}
              >
                ↻ Refresh subjects
              </Typography>
            </Box>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

export default StudentAssignmentHome;
